/**
 * Ads report health API (SellerOS).
 *   GET  /sellerPartner/report-health?date=YYYY-MM-DD                          matrix for a report date
 *   GET  /sellerPartner/report-health/attempts?date=&profile_id=&report=        log rows + payload rows of one cell
 *   GET  /sellerPartner/report-health/saved?date=YYYY-MM-DD                    Saved data view (payload table)
 *   GET  /sellerPartner/report-health/saved/rows?date=&profile_id=&report=     payload rows of one cell
 *   GET  /sellerPartner/report-health/payload?id=<payload row id>[&download=1]  saved data
 *   POST /sellerPartner/report-health/retry       {date, profile_id, report}   retry one report
 *   POST /sellerPartner/report-health/retry-bulk  {date, profile_id?}          retry every failed report (store or all)
 *   GET  /sellerPartner/report-health/retry-status?task_id=                    progress of a retry
 */
import { CURRENT_API_URL } from "../../../Utils/Constants";

const API_ROOT = (
  CURRENT_API_URL ||
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_BASE_URL) ||
  "https://api.app.simpliworks.io"
).replace(/\/$/, "");

const config = {
  baseUrl: `${API_ROOT}/sellerPartner`,
  getAuthHeader: () => {
    let token = typeof window !== "undefined" ? window.localStorage.getItem("token") : null;
    if (!token && typeof window !== "undefined" && window.sessionStorage) {
      try {
        const persisted = window.sessionStorage.getItem("persist:root");
        if (persisted) {
          const root = JSON.parse(persisted);
          const loggedUser = root.loggedUser ? JSON.parse(root.loggedUser) : null;
          token = loggedUser && loggedUser.token;
        }
      } catch (e) {
        // ignore
      }
    }
    if (!token && typeof window !== "undefined" && window.store) {
      try {
        const state = window.store.getState();
        token = state && state.loggedUser && state.loggedUser.token;
      } catch (e) {
        // ignore
      }
    }
    if (token) {
      return token.startsWith("Token ") ? token : `Token ${token}`;
    }
    return null;
  },
};

export function configureReportHealthService(overrides) {
  Object.assign(config, overrides);
}

function headers(json) {
  const h = { Accept: "application/json" };
  const auth = config.getAuthHeader();
  if (auth) h.Authorization = auth;
  if (json) h["Content-Type"] = "application/json";
  return h;
}

async function call(path, body) {
  const res = await fetch(`${config.baseUrl}${path}`, {
    method: body ? "POST" : "GET",
    headers: headers(!!body),
    credentials: "same-origin",
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = {};
  try {
    data = await res.json();
  } catch (e) {
    data = {};
  }
  if (!res.ok) throw new Error(data.error || data.detail || `HTTP ${res.status}`);
  return data;
}

const enc = encodeURIComponent;

export const reportHealthService = {
  getDay: (date, fresh) => call(`/report-health?date=${enc(date)}${fresh ? "&fresh=1" : ""}`),
  getAttempts: (date, profileId, report) =>
    call(`/report-health/attempts?date=${enc(date)}&profile_id=${enc(profileId)}&report=${enc(report)}`),
  getSaved: (date, fresh) => call(`/report-health/saved?date=${enc(date)}${fresh ? "&fresh=1" : ""}`),
  getSavedRows: (date, profileId, report) =>
    call(`/report-health/saved/rows?date=${enc(date)}&profile_id=${enc(profileId)}&report=${enc(report)}`),
  getPayload: (id) => call(`/report-health/payload?id=${enc(id)}`),
  /** Downloads the whole saved data (sent with the auth header, so a plain link would not work). */
  downloadPayload: async (id, filename) => {
    const res = await fetch(`${config.baseUrl}/report-health/payload?id=${enc(id)}&download=1`, {
      headers: headers(false),
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const url = URL.createObjectURL(await res.blob());
    const a = Object.assign(document.createElement("a"), {
      href: url,
      download: filename || `payload-${id}.json`,
    });
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
  /** -> { task_id, queued, skipped } */
  retryReport: (date, profileId, report) =>
    call("/report-health/retry", { date, profile_id: profileId, report }),
  /** profileId omitted = every store of the date -> { task_id, queued, skipped } */
  retryBulk: (date, profileId) =>
    call("/report-health/retry-bulk", profileId ? { date, profile_id: profileId } : { date }),
  retryStatus: (taskId) => call(`/report-health/retry-status?task_id=${enc(taskId)}`),
};

export default reportHealthService;
